import { ComponentProps, ReactNode, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { CheckboxField, FormRow, SwitchField } from '@frontend/design-system';
import { useRuleRecurrenceForm } from '../../../../hooks/settings/use-rule-recurrence-form';
import { AvailabilityFormRow } from '../atoms/availability-form-row';
import { PreviewPatternFormValues } from '../helpers/get-preview-pattern-description';
import { NonRecurrenceFields } from './non-recurrence-fields';
import { RecurrenceFields } from './recurrence-fields';

type FieldPropsGetter = (...args: any[]) => Record<string, unknown>;

type RecurrenceFormBaseValues = PreviewPatternFormValues & {
  recurrenceType?: string;
  isAllDay?: boolean;
  isRecurring?: boolean;
};

type Props<TFieldProps extends FieldPropsGetter, TValues extends RecurrenceFormBaseValues> = {
  locationId: string | undefined;
  formProps: ReturnType<typeof useRuleRecurrenceForm<TFieldProps, TValues>>;
  isRecurring: boolean;
  switchLabel: string;
  switchFieldProps?: Omit<ComponentProps<typeof SwitchField>, 'label'>;
  allDayFieldProps?: Omit<ComponentProps<typeof CheckboxField>, 'label'>;
  endDateLabel?: ReactNode;
};

export const AvailabilityScheduleForm = <
  TFieldProps extends FieldPropsGetter,
  TValues extends RecurrenceFormBaseValues
>({
  locationId,
  formProps,
  isRecurring,
  switchLabel,
  switchFieldProps,
  allDayFieldProps,
  endDateLabel,
}: Props<TFieldProps, TValues>) => {
  const { t } = useTranslation('schedule-settings');
  const { formValues, getAvailabilityRuleFieldProps: getFieldProps } = formProps;

  const isAllDay = allDayFieldProps?.value as boolean | undefined;

  const previewPatternFormValues = useMemo<PreviewPatternFormValues>(
    () => ({
      startDate: formValues.startDate,
      endDate: formValues.endDate,
      startTime: isAllDay ? null : formValues.startTime ?? '',
      endTime: isAllDay ? null : formValues.endTime ?? '',
    }),
    [formValues.startDate, formValues.endDate, formValues.startTime, formValues.endTime, isAllDay]
  );

  return (
    <>
      {/* TODO: Remove the conditional rendering of switch field once API supports non-recurring availability for Availability Rules */}
      {switchFieldProps && (
        <FormRow>
          <SwitchField label={switchLabel} {...switchFieldProps} />
        </FormRow>
      )}
      {isRecurring ? (
        <RecurrenceFields
          locationId={locationId}
          formProps={formProps}
          previewPatternFormValues={previewPatternFormValues}
          timeFieldsDisabled={isAllDay}
          endDateLabel={endDateLabel}
          additionalTimeRowContent={
            allDayFieldProps ? (
              <AvailabilityFormRow>
                <CheckboxField {...allDayFieldProps} label={t('All-Day')} />
              </AvailabilityFormRow>
            ) : undefined
          }
        />
      ) : (
        <NonRecurrenceFields getFieldProps={getFieldProps} allDayFieldProps={allDayFieldProps} />
      )}
    </>
  );
};
